/**
 * {@link LeaderboardSettings} — toggle leaderboard visibility between
 * "public" (anyone can view) and "authenticated" (login required).
 *
 * Requirements: 9.1
 */

import { useState } from 'react';
import { ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export type LeaderboardVisibility = 'public' | 'authenticated';

export interface LeaderboardSettingsProps {
  eventId: string;
  apiClient: ApiClient;
  currentVisibility: LeaderboardVisibility;
  /** Called after a successful visibility change so the parent can refresh. */
  onVisibilityChange: (visibility: LeaderboardVisibility) => void;
}

const containerStyle: CSSProperties = {
  padding: '1rem',
  background: 'var(--color-bg-page, #FAFBFC)',
  borderRadius: 'var(--radius-button, 8px)',
  marginBottom: '1rem',
};

const headingStyle: CSSProperties = {
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--color-text-primary, #1A1A2E)',
};

const optionGroupStyle: CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const radioLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  cursor: 'pointer',
  padding: '0.375rem 0.75rem',
  borderRadius: 'var(--radius-button, 8px)',
  border: '1px solid var(--color-border, #E5E7EB)',
  background: 'var(--color-bg-surface, #FFFFFF)',
};

const radioLabelActiveStyle: CSSProperties = {
  ...radioLabelStyle,
  borderColor: 'var(--color-brand-green)',
  background: '#e6f4ea',
};

const descriptionStyle: CSSProperties = {
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  color: 'var(--color-text-secondary, #6B7280)',
  marginTop: '0.5rem',
};

export function LeaderboardSettings({
  eventId,
  apiClient,
  currentVisibility,
  onVisibilityChange,
}: LeaderboardSettingsProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleChange(visibility: LeaderboardVisibility): Promise<void> {
    if (visibility === currentVisibility) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.request<unknown>(
        'PUT',
        `/events/${encodeURIComponent(eventId)}`,
        { body: { leaderboardVisibility: visibility } },
      );
      onVisibilityChange(visibility);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to update leaderboard visibility.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={containerStyle} aria-labelledby="leaderboard-settings-heading">
      <div style={headingStyle} id="leaderboard-settings-heading">
        Leaderboard Visibility
      </div>

      {error && (
        <ErrorBanner title="Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      <div style={optionGroupStyle} role="radiogroup" aria-label="Leaderboard visibility">
        <label
          style={currentVisibility === 'public' ? radioLabelActiveStyle : radioLabelStyle}
        >
          <input
            type="radio"
            name="leaderboard-visibility"
            value="public"
            checked={currentVisibility === 'public'}
            onChange={() => { void handleChange('public'); }}
            disabled={saving}
            aria-label="Public — anyone can view"
          />
          Public
        </label>
        <label
          style={currentVisibility === 'authenticated' ? radioLabelActiveStyle : radioLabelStyle}
        >
          <input
            type="radio"
            name="leaderboard-visibility"
            value="authenticated"
            checked={currentVisibility === 'authenticated'}
            onChange={() => { void handleChange('authenticated'); }}
            disabled={saving}
            aria-label="Authenticated — login required"
          />
          Authenticated
        </label>
        {saving && <Spinner size="1rem" label="Saving…" />}
      </div>

      <p style={descriptionStyle}>
        {currentVisibility === 'public'
          ? 'Anyone can view the leaderboard without logging in.'
          : 'Users must be logged in to view the leaderboard.'}
      </p>
    </div>
  );
}
